import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight, Copy, Check, Github } from 'lucide-react';
import { FlipPhoto, MagneticButton } from './ui';
import { GITHUB_URL } from '../data/work';

const EMAIL = 'info@ethanzhou.ca';

/*
 * A copy button, not a mailto.
 *
 * The original CTA was a bare `mailto:`, which silently does nothing for
 * anyone without a default mail client registered. That is most people
 * reading webmail in a browser tab, so the button just looked broken.
 *
 * Now the label is the address, pressing it copies, and it says so.
 * execCommand is kept as a fallback because navigator.clipboard is undefined
 * outside a secure context.
 */
function EmailButton() {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = useCallback(() => {
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  }, []);

  const copy = useCallback(() => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(EMAIL).then(flash, legacyCopy);
      return;
    }
    legacyCopy();

    function legacyCopy() {
      const el = document.createElement('textarea');
      el.value = EMAIL;
      el.setAttribute('readonly', '');
      el.style.cssText = 'position:absolute;left:-9999px';
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
        flash();
      } finally {
        document.body.removeChild(el);
      }
    }
  }, [flash]);

  return (
    <button
      type="button"
      onClick={copy}
      data-hover
      aria-label={`Copy email address ${EMAIL}`}
      /* Fixed min-width: the label swap shrinks the button from ~205px to
         ~119px otherwise, which shoves the github button sideways. */
      className="inline-flex min-w-[13rem] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-slate-900 px-6 py-3 text-sm font-medium text-white shadow-card transition-all duration-300 hover:shadow-card-hover active:translate-y-px dark:bg-slate-50 dark:text-slate-900"
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      <span>{copied ? 'copied' : EMAIL}</span>
      {/* Announced to screen readers without shifting the visible label. */}
      <span aria-live="polite" className="sr-only">
        {copied ? 'Email address copied to clipboard' : ''}
      </span>
    </button>
  );
}

/*
 * Hero.
 *
 * Replaces the old centred stack (Caveat handwriting headline, "eng student
 * who makes things on the internet", three pill buttons in a row). That version
 * said nothing specific and looked like every other student portfolio.
 *
 * Layout is an asymmetric split at DESIGN_VARIANCE 7: copy left, portrait
 * right, collapsing to a single column under md.
 *
 * Hero stack discipline: 3 text elements (headline, subtext, CTAs). No eyebrow,
 * no trust strip, no scroll cue.
 */
export default function Home({ navigate }) {
  return (
    <>
      <section className="mx-auto flex min-h-[calc(100dvh-68px)] max-w-5xl flex-col justify-center px-6 pt-8 pb-16 md:pt-16">
        <div className="grid items-center gap-10 md:grid-cols-[1.35fr_0.65fr] md:gap-16">
          <div>
            {/* Plain text, not per-letter spans. ProximityText marked every
                letter aria-hidden and appended an sr-only copy of the whole
                line, so selecting the headline picked up both and pasting gave
                you "i build software,i build software,". */}
            <h1
              className="rise rise-1 text-[2.4rem] font-semibold leading-[1.08] tracking-tight text-slate-900 dark:text-slate-50 sm:text-[3.1rem] md:text-[3.5rem]"
            >
              hi, i&rsquo;m ethan.
            </h1>

            <div className="rise rise-3 mt-9 flex flex-wrap items-center gap-3">
              <MagneticButton>
                <EmailButton />
              </MagneticButton>

              <MagneticButton>
                <a
                  href={GITHUB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-hover
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-slate-300 px-6 py-3 text-sm font-medium text-slate-700 transition-all duration-300 hover:border-slate-400 hover:bg-white active:translate-y-px dark:border-slate-700 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:bg-slate-900"
                >
                  <Github className="h-4 w-4" />
                  github
                </a>
              </MagneticButton>
            </div>
          </div>

          <div className="rise rise-4 order-first flex justify-center md:order-none md:justify-end">
            <FlipPhoto className="h-40 w-40 sm:h-52 sm:w-52 md:h-60 md:w-60" />
          </div>
        </div>
      </section>

      <TwoTracks navigate={navigate} />
    </>
  );
}

/*
 * The two tracks, weighted equally.
 *
 * A 50/50 grid rather than a hierarchy, because the brief was that the
 * building and the audience carry the same weight.
 */
function TwoTracks({ navigate }) {
  const tracks = [
    {
      key: 'work',
      kicker: 'the building',
      title: 'developer tools for mobile teams',
      body: 'ci bots, scanners and sdks that catch what manual testing misses.',
      stat: '20+',
      statLabel: 'public repos',
      cta: 'see the work',
      route: 'work',
    },
    {
      key: 'content',
      kicker: 'the audience',
      title: 'short finance videos, made since 2023',
      body: 'short explainers about investing, under @ethanzhouwealth.',
      stat: '10M+',
      statLabel: 'views',
      cta: 'see the content',
      route: 'content',
    },
  ];

  return (
    <section className="mx-auto max-w-5xl px-6 pb-24">
      <div className="grid gap-4 md:grid-cols-2 md:gap-5">
        {tracks.map((t, i) => (
          <button
            key={t.key}
            type="button"
            onClick={() => navigate(t.route)}
            data-hover
            className={`rise rise-${i + 4} group relative flex flex-col rounded-card border border-slate-200 bg-white/70 p-7 text-left transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-card-hover dark:border-slate-800 dark:bg-slate-900/50 dark:hover:border-slate-700 sm:p-8`}
          >
            <span className="text-xs uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
              {t.kicker}
            </span>

            <h2 className="mt-4 text-xl font-semibold leading-snug tracking-tight text-slate-900 dark:text-slate-50 sm:text-2xl">
              {t.title}
            </h2>

            <p className="mt-3 max-w-[42ch] flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              {t.body}
            </p>

            <div className="mt-7 flex items-end justify-between gap-4 border-t border-slate-200/80 pt-5 dark:border-slate-800">
              <div>
                <div className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
                  {t.stat}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-500">
                  {t.statLabel}
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400">
                {t.cta}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </div>
          </button>
        ))}
      </div>

      <p className="rise rise-5 mt-10 text-sm text-slate-500 dark:text-slate-500">
        <button
          onClick={() => navigate('about')}
          data-hover
          className="inline-flex items-center gap-1 font-medium text-slate-900 underline decoration-slate-300 underline-offset-4 transition-colors hover:decoration-slate-900 dark:text-slate-100 dark:decoration-slate-600 dark:hover:decoration-slate-100"
        >
          more about me
          <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </p>
    </section>
  );
}
